import { z } from 'zod';

/** A CUID2-ish opaque id. We don't validate the exact shape, just non-empty string. */
export const Id = z.string().min(1).max(64).brand<'Id'>();
export type Id = z.infer<typeof Id>;

export const Email = z.string().trim().toLowerCase().email().max(254);

/** Indian mobile number in E.164 (+91XXXXXXXXXX). Accepts a bare 10-digit form and normalises. */
export const PhoneIN = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(
    z
      .string()
      .regex(/^(\+91)?[6-9]\d{9}$/, 'Must be a valid Indian mobile number')
      .transform((v) => (v.startsWith('+91') ? v : `+91${v}`)),
  );

export const Password = z
  .string()
  .min(8, 'At least 8 characters')
  .max(128)
  .regex(/[a-z]/, 'Needs a lowercase letter')
  .regex(/[A-Z]/, 'Needs an uppercase letter')
  .regex(/\d/, 'Needs a digit');

/** Monetary amount: non-negative integer **paise**. Never floats for money. */
export const Paise = z.number().int().nonnegative().brand<'Paise'>();
export type Paise = z.infer<typeof Paise>;

export const Currency = z.literal('INR');

export const Money = z.object({ amount: Paise, currency: Currency.default('INR') });
export type Money = z.infer<typeof Money>;

export const Pincode = z.string().regex(/^[1-9]\d{5}$/, 'Invalid Indian PIN code');

/** GSTIN — 15 chars: 2 state + 10 PAN + 1 entity + 'Z' + 1 checksum. */
export const GSTIN = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN');

export const PAN = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{5}\d{4}[A-Z]$/, 'Invalid PAN');

/** HSN (goods, 4/6/8 digits) or SAC (services, 6 digits). */
export const HSN_SAC = z.string().regex(/^\d{4,8}$/, 'Invalid HSN/SAC code');

export const Slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Invalid slug');

export const IsoDateTime = z.string().datetime({ offset: true });

export const PercentBps = z.number().int().min(0).max(10_000); // basis points: 0..100%

/**
 * Strip HTML tags from a free-text field the storefront/admin/vendor apps
 * render directly (product titles, vendor store names, saved addresses).
 *
 * These values are stored, then later interpolated into HTML built outside
 * React's normal escaping — printable invoices, notification templates,
 * server-rendered emails — so a stored `<script>`/`onerror=` payload becomes
 * XSS wherever that happens, not just wherever the field is first echoed
 * back. Stripping at write time closes that off at the source rather than
 * depending on every downstream renderer remembering to escape. Any
 * consumer that also happens to escape (React JSX, or an explicit
 * `escapeHtml()` at a `document.write`-style sink) is unaffected — this
 * only ever removes characters, never changes what a plain-text value reads
 * as.
 *
 * Mirrors the sanitizer `apps/api/src/modules/products/products.routes.ts`
 * has applied to product fields since before this helper existed:
 *   1. Drop `<script>` / `<style>` blocks, including their content.
 *   2. Drop every remaining tag (`<...>`).
 *   3. Collapse HTML entities back to their characters (`&amp;` -> `&`).
 *   4. Collapse runs of whitespace.
 */
export function stripHtml(input: string): string {
  const noBlocks = input.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '');
  const noTags = noBlocks.replace(/<[^>]+>/g, '');
  const decoded = noTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  return decoded.replace(/\s+/g, ' ').trim();
}

/** A trimmed, HTML-stripped free-text field with a max length. */
export const PlainText = (max: number, min = 0) =>
  z
    .string()
    .transform(stripHtml)
    .pipe(min > 0 ? z.string().min(min).max(max) : z.string().max(max));
